import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, TennisBall } from '@phosphor-icons/react';

export default function RulesPage() {
  const navigate = useNavigate();

  return (
    <div className="space-y-8" data-testid="rules-page">
      <div>
        <h1 className="font-serif text-4xl sm:text-5xl text-[#013523] tracking-tight">Rules</h1>
        <p className="text-sm text-[#6a766e] mt-1">How the tennis ladder works</p>
      </div>

      <RuleSection number="1" title="Overview">
        <p>This is a private, invite-only tennis ladder. Players move up the rankings by challenging higher-seeded players and winning matches.</p>
        <ul className="list-disc list-inside space-y-1.5 mt-3">
          <li>Enable SMS notifications in Settings if you want alerts when you&apos;re challenged.</li>
        </ul>
      </RuleSection>

      <RuleSection number="2" title="Challenge Eligibility">
        <ul className="list-disc list-inside space-y-1.5">
          <li>You may only challenge players up to <strong>4 spots above</strong> your current rank.</li>
          <li>Each player may have only <strong>1 outgoing challenge</strong> and <strong>1 incoming challenge</strong> at a time.</li>
        </ul>
      </RuleSection>

      <RuleSection number="3" title="Challenge Timing">
        <ul className="list-disc list-inside space-y-1.5">
          <li>The challenged player has <strong>72 hours</strong> to accept.</li>
          <li>If not accepted within 72 hours, the challenge expires with no penalties.</li>
          <li>Once accepted, the match stays active until the score is entered — <strong>no completion deadline</strong>.</li>
        </ul>
      </RuleSection>

      <RuleSection number="4" title="Match Results">
        <ul className="list-disc list-inside space-y-1.5">
          <li>Either player may submit the match score.</li>
          <li>The opponent must <strong>approve</strong> the submitted score.</li>
          <li>Rankings update <strong>only after</strong> the score is approved.</li>
          <li>If a score is rejected, the match returns to pending score entry until resubmitted and approved.</li>
        </ul>
      </RuleSection>

      <RuleSection number="5" title="Ranking Movement">
        <ul className="list-disc list-inside space-y-1.5">
          <li>If the <strong>challenger wins</strong>, they take the higher-ranked player's seed. The losing player and everyone below shift down.</li>
          <li>If the <strong>challenger loses</strong>, rankings do not change.</li>
        </ul>
      </RuleSection>

      <RuleSection number="6" title="Pause Rules">
        <p className="mb-2">Paused players remain visible in the rankings but are marked as Paused. They cannot challenge or be challenged.</p>
        <p className="mb-2">On return, a penalty applies based on pause length:</p>
        <div className="bg-[#f0ece3] rounded-xl border border-[#e3dcd1] overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-[#e3dcd1]">
                <th className="text-left px-4 py-2 text-xs uppercase tracking-wider text-[#6a766e] font-medium">Duration</th>
                <th className="text-left px-4 py-2 text-xs uppercase tracking-wider text-[#6a766e] font-medium">Penalty</th>
              </tr>
            </thead>
            <tbody className="text-[#2a332c]">
              <tr className="border-b border-[#e3dcd1]/50"><td className="px-4 py-2">1 – 7 days</td><td className="px-4 py-2">No penalty</td></tr>
              <tr className="border-b border-[#e3dcd1]/50"><td className="px-4 py-2">8 – 14 days</td><td className="px-4 py-2">Drop 1 seed</td></tr>
              <tr className="border-b border-[#e3dcd1]/50"><td className="px-4 py-2">15 – 21 days</td><td className="px-4 py-2">Drop 2 seeds</td></tr>
              <tr className="border-b border-[#e3dcd1]/50"><td className="px-4 py-2">22 – 30 days</td><td className="px-4 py-2">Drop 3 seeds</td></tr>
              <tr><td className="px-4 py-2">31+ days</td><td className="px-4 py-2">Move to last seed</td></tr>
            </tbody>
          </table>
        </div>
      </RuleSection>

      <RuleSection number="7" title="Inactivity Rules">
        <ul className="list-disc list-inside space-y-1.5">
          <li>If a player completes no recorded match in <strong>30 days</strong>, they move down <strong>4 spots</strong>.</li>
          <li>If near the bottom, they move to last place.</li>
        </ul>
      </RuleSection>

      <RuleSection number="8" title="Admin Authority">
        <p>The ladder administrator may resolve disputes, edit results, adjust rankings, manage invites, and override system actions when needed.</p>
      </RuleSection>
    </div>
  );
}

function RuleSection({ number, title, children }) {
  return (
    <div className="animate-fade-in-up" style={{ animationDelay: `${number * 0.03}s`, opacity: 0 }}>
      <div className="flex items-baseline gap-3 mb-2">
        <span className="text-xs font-mono text-[#c2a251] bg-[#c2a251]/10 rounded-full w-6 h-6 flex items-center justify-center shrink-0">{number}</span>
        <h2 className="font-serif text-xl text-[#013523]">{title}</h2>
      </div>
      <div className="text-sm text-[#2a332c] leading-relaxed pl-9">
        {children}
      </div>
    </div>
  );
}
